import React, { useEffect, useState, useRef } from "react";
import { Slider, Popover, Checkbox, Col, Row, Modal } from "antd";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { UpdateCriteriaRows } from "redux/DecisionMatrix/UpdateCriteriaRows/action";

import { ScoringRubricModal } from "scenes/DecisionMatrix/components/ScoringRubricModal/scoringRubricModal";
import { ClosePopup } from "redux/DecisionMatrix/GetDecisionDetails/action";

// Todo: delete these from redux?
/* import { DeleteCriteria } from "redux/DecisionMatrix/DeleteCriteria/action";
import { AssignCriteria } from "redux/DecisionMatrix/AssignCriteria/action";
import { UpdateCriteria } from "redux/DecisionMatrix/UpdateCriteria/action";
import { AddCriteriaRows } from "redux/DecisionMatrix/AddCriteriaRows/action"; */

import classes from "./manageCriteria.module.scss";
import cc from "classnames";

import { ReactComponent as CloseIcon2 } from "./close_2.svg";
import { ReactComponent as AddIcon2 } from "./add_2.svg";
import { ReactComponent as OptionsIcon } from "./options.svg";
import { ReactComponent as NextIcon } from "./next_icon.svg";
import { ReactComponent as BackIcon } from "./back.svg";
import { ReactComponent as MoveIcon } from "./move.svg";
import { ReactComponent as CautionIcon } from "./caution.svg";
import { weightDistributionV4 } from "utils/utility";
import { AIGenerateCriteriaModal } from "../AIAssistantModal/AIGenerateCriteriaModal";
import { AIScoringRubicModal } from "../AIAssistantModal/AIScoringRubicModal";
import { AIDefinitionModal } from "../AIAssistantModal/AIDefinitionModal";

const ManageCriteria = () => {
  const { decisionId } = useParams();

  const dispatch = useDispatch();

  const { success: userDecisions, loading } = useSelector(
    (state) => state.getDecisionDetailsReducer
  );

  const { loading: updateLoading } = useSelector(
    (state) => state.updateCriteriaRowsReducer
  );
  const [criterias, setCriterias] = useState([]);
  const [isWeightInput, setIsWeightInput] = useState("");
  const [isShowInactive, setIsShowInactive] = useState(false);
  const [isViewRubric, setIsViewRubric] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isRubricModal, setIsRubricModal] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [isDefinitionModal, setIsDefinitionModal] = useState(false);
  const [steps, setSteps] = useState(0);
  const [popoverID, setPopoverID] = useState(null);
  const [popover1ID, setPopover1ID] = useState(null);
  const [stepDescriptionsArr, setStepDescriptionsArr] = useState([
    "Establish and Define Criteria",
    "Prioritize Criteria",
    "Weigh criteria",
    "Define Scoring Rubric",
  ]);
  const [ids, setIds] = useState([]);
  const [rubricIds, setRubricIds] = useState([]);
  const [isGenerateCriteriaModal, setIsGenerateCriteriaModal] = useState(false);
  const [ischange, setIsChange] = useState([])
  const [sortedCriteria, setSortedCriteria] = useState([])
  // Use a ref to store previous criteria for comparison
  const prevCriteriaRef = useRef(null);


  useEffect(() => {
    // Skip processing if no criteria
    if (!userDecisions?.criteria) return;

    // For large criteria sets (>50), check if criteria have actually changed
    if (userDecisions.criteria.length > 50) {
      // Simple check to see if criteria have changed in a meaningful way
      // Only compare IDs, weights, active status, and is_deleted flags
      const criteriaSignature = userDecisions.criteria.map(c =>
        `${c.id}-${c.weight}-${c.active}-${c.is_deleted}`
      ).join('|');

      // If criteria signature hasn't changed, skip processing
      if (prevCriteriaRef.current === criteriaSignature) {
        return;
      }

      // Update the ref with current signature
      prevCriteriaRef.current = criteriaSignature;
    }

    // Continue with normal processing
    const totalWeight = userDecisions.criteria.reduce((acc, curr) => {
       if (!curr?.active || curr?.is_deleted) return acc;
       const weight = typeof curr.weight === 'string' ? parseFloat(curr.weight || 0) || 0 : (curr.weight || 0);
       return acc + weight;
    }, 0);

    // Create a copy to avoid mutating the original array
    const criteriaToProcess = [...userDecisions.criteria];

    // Common transformation for criteria
    const sortedAndMappedCriteria = criteriaToProcess
      .sort((a, b) => (b?.weight || 0) - (a?.weight || 0))
      .map((c, i) => ({ ...c, order: i }));

    setCriterias(
      totalWeight !== 100
        ? weightDistributionV4(sortedAndMappedCriteria, 100)
        : sortedAndMappedCriteria
    );
  }, [userDecisions?.criteria]);

  useEffect(() => {
    return () => {
      setIds([]);
    };
  }, []);

  useEffect(() => {
    const sorted = [...criterias].sort((a, b) => {
      const orderA = Number(a.order ?? Infinity);
      const orderB = Number(b.order ?? Infinity);
      return orderA - orderB;
    });

    setSortedCriteria(sorted);
  }, [criterias]);

  const setField = (e, field) => {
    if (e?.title?.length > 0) {
      setIsChange((prevIds) => [...prevIds, e?.id])
    } else {
      setIsChange((prevIds) => prevIds.filter((id) => id !== e?.id))
    }
    if (e?.title?.replaceAll('\n', '')?.length > 255) {
      // Function to push a new ID into the state
      setIds((prevIds) => [...prevIds, e?.id]);
    } else {
      // Function to pop the specifi ID from the state
      setIds((prevIds) => prevIds.filter((id) => id !== e?.id))
    }
    if (e?.rubric?.replaceAll('\n', '')?.length > 2000) {
      // Function to push a new ID into the state
      setRubricIds((prevIds) => [...prevIds, e?.id]);
    } else {
      // Function to pop the specifi ID from the state
      setRubricIds((prevIds) => prevIds.filter((id) => id !== e?.id))
    }
    setCriterias(
      criterias?.map((criteria) =>
        criteria?.id === e?.id ? { ...criteria, [field]: e[field] } : criteria
      )
    );
  };

  const activeCriterias = criterias?.filter((c) => c?.active && !c?.is_deleted);
  const inactiveCriterias = criterias?.filter(
    (c) => !c?.active && !c?.is_deleted
  );

  // Calculate total weight with proper rounding to avoid floating point issues
  const totalWeight = Math.round(activeCriterias?.reduce((c, i) => {
    // Handle both string and number weights by parsing to float
    const weight = typeof i?.weight === 'string' ? parseFloat(i?.weight || 0) || 0 : (i?.weight || 0);
    return c + weight;
  }, 0));


  const onSliderChangeHandle = (id) => (value) => {
    // Get a copy of all active criteria
    let activeCriteriasCopy = criterias
      ?.filter((c) => c?.active && !c.is_deleted)
      ?.map((obj) => ({ ...obj }));

    // Find the index of the criteria being adjusted
    const index = activeCriteriasCopy?.findIndex((obj) => obj?.id === id);

    if (index < 0) return; // Exit if criteria not found

    // Update the weight of the selected criteria
    activeCriteriasCopy[index].weight = value;

    // Only validate that value is not negative
    if (value < 0) return;

    // Calculate remaining weight to distribute
    const remainingWeight = 100 - value;

    // Get all criteria except the one being adjusted
    const otherCriteria = activeCriteriasCopy.filter(c => c.id !== id);

    // If there are other criteria, redistribute the remaining weight
    if (otherCriteria.length > 0) {
      // Use weightDistributionV4 to redistribute the remaining weight among other criteria
      const redistributedCriteria = weightDistributionV4(otherCriteria, remainingWeight);

      // Combine the adjusted criteria with the redistributed ones
      activeCriteriasCopy = redistributedCriteria
      .concat(activeCriteriasCopy[index])
      .sort((a, b) => a.order - b.order);   // keeps UI stable
    }

    // Calculate the new total weight to verify it's 100%
    const newTotalWeight = Math.round(
      activeCriteriasCopy.reduce((acc, curr) => acc + curr.weight, 0)
    );

    // Only update if the total weight is 100% and all criteria have defined weights
    if (
      newTotalWeight === 100 &&
      !activeCriteriasCopy.find((criteria) => criteria.weight === undefined)
    ) {
      // Update the state with the new criteria weights
      setCriterias([
        ...activeCriteriasCopy,
        ...criterias.filter((c) => !c?.active || c?.is_deleted)
      ]);
    }
  };

  const onAddCriteriaHandle = () => {
    const criteriasModified = [
      ...criterias,
      {
        decision: decisionId,
        title: "",
        description: "",
        rubric: "",
        weight: 100 - totalWeight > 0 ? 100 - totalWeight : 1,
        active: true,
        id: new Date()?.getTime(),
        order: Math.max(...criterias.map(c => c.order || 0), 0) + 1
      },
    ];

    const criteriasWeight = criteriasModified.filter(
      (c) => c?.active && !c?.is_deleted
    );
    const criteriasFinal = weightDistributionV4(criteriasWeight, 100);

    setCriterias([
      ...(criteriasModified || []).filter((c) => c.is_deleted || !c.active),
      ...criteriasFinal,
    ]);

    setTimeout(() => {
      const result = document.getElementsByClassName("criteria-name");
      const lastItem = result[result?.length - 1];

      lastItem?.focus();
    }, 150);
  };

  const onShowInactiveToggleHandle = () => {
    setIsShowInactive(!isShowInactive);
  };

  const handleOpenChange = (visible) => {
    if (!visible) {
      setPopoverID(null);
    }
  };

  const DeleteCriteria = (criteria) => {
    setPopoverID(null);

    if (criteria?.created_at) {
      const criteriasPrepare = criterias?.map((c) =>
        c?.id === criteria?.id ? { ...c, is_deleted: true } : c
      );

      const criteriasWeight = criteriasPrepare?.filter(
        (c) =>  !c?.is_deleted
      );
      const criteriasFinal = weightDistributionV4(criteriasWeight, 100);

      setCriterias([
        ...(criteriasPrepare?.filter(c => c?.is_deleted) ?? []),
        ...criteriasFinal,
      ]);

    } else {
      const criteriasPrepare = criterias?.filter((c) => c?.id !== criteria?.id);

      const criteriasWeight = criteriasPrepare?.filter(
        (c) => !c?.is_deleted
      );
      const criteriasFinal = weightDistributionV4(criteriasWeight, 100);

      setCriterias([
        ...(criteriasPrepare?.filter(c => c?.is_deleted) ?? []),
        ...criteriasFinal,
      ]);

    }
  };

  const deleteCriteriaHandle = criteria => () => {
    let updatedCriteria = criterias?.map((item) =>
      item?.id === criteria?.id ? { ...item, is_deleted: true } : item
    );

    setCriterias(updatedCriteria);
    DeleteCriteria(criteria);
  };

  const onActivateToggleHandle = (criteria) => () => {
    setPopoverID(null);

    const prepareCriterias = criterias?.map((c) =>
      c?.id === criteria?.id ? { ...c, active: !c?.active } : c
    );

    setCriterias([
      ...weightDistributionV4(
        prepareCriterias
          ?.filter((c) => c?.active && !c?.is_deleted)
          ?.map((c, i) => ({ ...c, order: i })),
        100
      ),
      ...prepareCriterias?.filter((c) => !c?.active || c?.is_deleted),
    ]);
  };

  const onRubricSaveHandle = (criteria, rubric) => {
    setCriterias(
      criterias?.map((c) => (c?.id === criteria ? { ...c, rubric } : c))
    );

    setIsViewRubric(null);
  };
  const onDefinitionApplyModal = (criteria, description) => {
    setCriterias(
      criterias?.map((c) =>
        c?.id === criteria?.id ? { ...c, description } : c
      )
    );
    setSelectedCriteria(null);
    setIsDefinitionModal(false);
  };
  const handleOpenChange1 = (visible) => {
    if (!visible) {
      setPopover1ID(null);
    }
  };
  const onDefinitionModal = (criteria) => {
    // Bail out if title is blank/whitespace
    if (!criteria?.title?.trim()) return;
    setPopoverID(null);
    setSelectedCriteria({ id: criteria?.id, title: criteria?.title || "" });
    setIsDefinitionModal(true);
  };
  const saveCriterias = () => {
    const criteriasForCreate = criterias?.filter((c) => !c?.created_at && c?.title).sort((a, b) => a.order - b.order);
    const criteriasForUpdate = criterias?.filter((c) => {
      if (!c?.created_at ) return false;

      const o = userDecisions?.criteria?.find((c2) => c2?.id === c?.id);

      if (
        o.title === c.title &&
        o.description === c.description &&
        o.weight === c.weight &&
        o.active === c.active &&
        o.rubric === c.rubric
      )
        return false;

      return true;
    });

    const criteriasForDelete = criterias
      ?.filter((c) => c?.created_at && c?.is_deleted)
      ?.map((c) => c?.id);

    dispatch(ClosePopup(userDecisions?.id));

    dispatch(
      UpdateCriteriaRows({
        create: criteriasForCreate,
        update: criteriasForUpdate,
        delete: criteriasForDelete,
        decision: decisionId,
      })
    );
  };

  const handleStepCount = (steps) => stepDescriptionsArr[steps];

  function moveItemInArray(array, from, to) {
    if (to === from) return array;
    var target = array[from];
    var increment = to < from ? -1 : 1;

    for (var k = from; k != to; k += increment) {
      array[k] = array[k + increment];
    }
    array[to] = target;
    return array;
  }
  const onRubricApplyModal = (criteria, rubric) => {
    setCriterias(
      criterias?.map((c) => (c?.id === criteria?.id ? { ...c, rubric } : c))
    );
    setSelectedCriteria(null);
    setIsRubricModal(false);
  };
  const onRubricModal = (criteria) => {
    setPopover1ID(null);
    setSelectedCriteria({ id: criteria?.id, title: criteria?.title });
    setIsRubricModal(true);
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    const newArray = criterias.filter((item) => !item.is_deleted && item?.active)
    const modifiedCriteriaArr = moveItemInArray(
      newArray,
      source?.index,
      destination?.index ?? newArray?.length - 1
    );

    // Assign orders in ascending order (1, 2, 3, …)
    const modifiedWithOrder = modifiedCriteriaArr.map((c, i) => ({
      ...c,
      order: i + 1,
    }));
    const inactiveOrDeleted = criterias.filter((c) => c.is_deleted || !c.active);
    setCriterias([
      ...inactiveOrDeleted,
      ...modifiedWithOrder,
    ]);

  };


  const onAddAICriteriaHandle = (list) => {
    if (!list) return;
    const criteriasModified = [
      ...criterias?.filter((item) => item?.title),
      ...criterias
        ?.filter((item) => !item?.title && !item?.description)
        ?.map((item) => {
          return { ...item, is_deleted: true };
        }),
    ];
    list?.forEach((element) => {
      criteriasModified?.push({
        decision: parseInt(decisionId),
        title: element?.criteria_name,
        description: element?.criteria_description,
        rubric: "",
        weight: 100 - totalWeight > 0 ? 100 - totalWeight : 1,
        active: true,
        id: uuidv4(),
        priority: null,
        order: criteriasModified?.length + 1,
      });
    });
    const criteriasWeight = criteriasModified.filter(
      (c) => c?.active && !c?.is_deleted
    );
    const criteriasFinal = weightDistributionV4(criteriasWeight, 100);

    setCriterias([
      ...criteriasModified.filter((c) => c?.is_deleted || !c?.active),
      ...criteriasFinal,
    ]);

    setIsGenerateCriteriaModal(false);
  };

  const definitionSteps = (criteria, isInactive) => (
    <div className={cc(classes.box, steps === 0 ? classes.box_popup : "")}>
      <textarea
        className={classes.textarea}
        placeholder={"Description..."}
        value={criteria?.description || ""}
        onChange={(e) =>
          setField(
            {
              description: e.target.value,
              id: criteria?.id,
            },
            "description"
          )
        }
      />
      {steps === 0 && (
        <Popover
          placement="left"
          overlayClassName={classes.dropdown}
          open={criteria?.id === popoverID}
          onOpenChange={handleOpenChange}
          content={
            <div className={classes.settings}>
              <button type="button" disabled={isInactive ? false : criterias.filter(item => !item.is_deleted && item.active).length <= 1} onClick={onActivateToggleHandle(criteria)}>
                {isInactive ? "Active" : "Inactive"}
              </button>
              {!isInactive && (
                <button
                type="button"
                onClick={() => onDefinitionModal(criteria)}
                disabled={criteria?.title?.replace(/\s/g, '')?.length === 0}
                >
                  Suggest Definition
                </button>
              )}
              <button type="button" disabled={isInactive ? false : criterias.filter(item => !item.is_deleted && item.active).length <= 1} onClick={deleteCriteriaHandle(criteria)}>
                Delete
              </button>
            </div>
          }
          trigger="click"
        >
          <span
            onClick={() => setPopoverID(criteria?.id)}
            className={classes.dropdownToggle}
          >
            <OptionsIcon />
          </span>
        </Popover>
      )}
    </div>
  );

  const weightStep = (criteria) => {
    // Check if there are more than 5 active criteria
    // const tooManyCriteria = activeCriterias?.length > 5;

    return (
      <div className={cc(classes.box, "d-flex align-items-center")}>
        <div className={cc(classes.sliderWrapper, "flex-grow-1")}>
          <div className={classes.slide_header}>
            <p>Weight</p>
            <span
              className={classes.sliderPercentage}
              onClick={() => setIsWeightInput(criteria?.id)}
            >
              {isWeightInput === criteria?.id ? (
                <input
                  className={classes.weightInput}
                  value={criteria?.weight}
                  type="text"
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow empty string or a single dot while typing
                    if (value === '' || value === '.' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                      // Check if value is <= 100 (if it's a valid number)
                      const numValue = parseFloat(value);
                      if (value === '' || value === '.' || isNaN(numValue) || numValue <= 100) {
                        setField(
                          {
                            weight: value,
                            id: criteria?.id,
                          },
                          "weight"
                        );
                      }
                    }
                  }}
                  onBlur={() => {
                    const floatVal = parseFloat(criteria?.weight);
                    if (!isNaN(floatVal)){

                      onSliderChangeHandle(criteria?.id)(floatVal);
                    }
                    setIsWeightInput("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const floatVal = parseFloat(criteria?.weight);
                      if (!isNaN(floatVal)) {
                        onSliderChangeHandle(criteria?.id)(floatVal);
                      }
                      setIsWeightInput("");
                    }
                  }}
                />

              ) : (
                <p>{(typeof criteria?.weight === 'string' && !isNaN(parseFloat(criteria?.weight)) ? parseFloat(criteria?.weight) : (parseFloat(criteria?.weight) || 0)).toFixed(2)}%</p>
              )}
            </span>
          </div>
          <Slider
            className="weight_slider"
            value={parseFloat(criteria?.weight)}
            defaultValue={parseFloat(criteria?.weight) || 0}
            min={0}
            max={100}
            step={0.1}
            onChange={onSliderChangeHandle(criteria?.id)}
          />
        </div>
      </div>
    );
  };

  const scoreScaleStep = (criteria) => (
    <div className={cc(classes.box, steps === 3 ? classes.box_popup : "")}>
      <div style={{ display: "flex", flexDirection: "column", width: "100%", marginTop: rubricIds.includes(criteria?.id) ? "10px" : "null" }}>
        <textarea
          className={cc(classes.textarea, classes.score_textarea)}
          placeholder={"6 to 10-Excellent, 3 to 5 Good ..."}
          value={criteria?.rubric || ""}
          onChange={(e) => {
            setField(
              {
                rubric: e?.target?.value,
                id: criteria?.id,
              },
              "rubric"
            );
          }}
          style={{ borderColor: rubricIds.includes(criteria?.id) ? '#f51e14' : null }}
        />
        {rubricIds.includes(criteria?.id) && (<div style={{ color: '#f51e14', fontSize: 12, marginLeft: "5px" }}>Only 2000 characters are allowed.</div>)}
      </div>
      {steps === 3 && (
        <Popover
          placement="left"
          open={popover1ID === criteria?.id}
          onOpenChange={handleOpenChange1}
          overlayClassName={classes.dropdown}
          content={
            <div className={classes.settings}>
              <button type="button" onClick={() => onRubricModal(criteria)}>
                Generate with AI
              </button>
            </div>
          }
          trigger="click"
        >
          <span
            onClick={() => setPopover1ID(criteria?.id)}
            className={classes.dropdownToggle}
          >
            <OptionsIcon />
          </span>
        </Popover>
      )}
    </div>
  );

  const criteriaNumStyleHandler = () => {
    switch (steps) {
      case 0:
        return;
      case 1:
        return classes.disabled_criteria_number;

      default:
        return classes.criteria_number;
    }
  };

  const handleCriteriaTitle = () => {
    switch (steps) {
      case 0:
        return "Definition";
      case 1:
        return "Definition";
      case 2:
        return "Criteria Weight";
      case 3:
        return "Score Scale Details";
      default:
        return "Definition";
    }
  };

  const criteriaHandler = (criteria, steps, isInactive) => {
    switch (steps) {
      case 0:
        return definitionSteps(criteria, isInactive);
      case 2:
        return weightStep(criteria);
      case 3:
        return scoreScaleStep(criteria);
      default:
        return definitionSteps(criteria);
    }
  };

  const NavButton = ({ text, onClick, icon, reverse, disabled }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        opacity: disabled ? 0.5 : 1
      }}
      className={cc(
        classes.btn,
        reverse ? classes.reverse_btn : "",
        disabled ? classes.disabled : ""
      )}
      data-testid={reverse ? "next" : "prev"}
    >
      <figure className={cc(classes.next_icon)}
        style={{ cursor: disabled ? "unset" : "pointer" }}
      >{icon}</figure>
      <span>{text}</span>
    </button>
  );

  const SaveButton = ({ text, savingText, onClick, loading, disabled }) => (
    <div className={classes.save_btn}>
      <button onClick={onClick} disabled={disabled} style={{ opacity: disabled ? 0.5 : 1 }}>
        {loading ? savingText : text}
      </button>
    </div>
  );

  const checkCriteriaTitle = () => {
    return criterias?.map(item => item.title).filter(item => item !== '').length === 0
  }

  if (!userDecisions || (userDecisions && !userDecisions.is_new))
    return false;
  // Determine if the Next button should be disabled based on current step and validation rules
  const isNextButtonDisabled = () => {
    // Common validation checks
    const hasInvalidIds = ids.length > 0;
    const hasEmptyCriteriaTitles = criterias?.some(item => (item?.active && !item.is_deleted && !item.title?.trim()));
    
    // Step-specific validation
    if (steps === 0 || steps === 1) {
      return checkCriteriaTitle() || hasInvalidIds || hasEmptyCriteriaTitles;
    }
    
    if (steps === 2) {
      return totalWeight !== 100 || loading || updateLoading || hasInvalidIds;
    }
    
    // For other steps (step 3)
    return hasInvalidIds || ischange.length === 0;
  };

  const handleNextClick = () => {
    const hasEmptyTitles = criterias?.some((item) => !item.is_deleted && item.active && !item.title?.trim());
    if (hasEmptyTitles) {
      return;
    }

    if (steps === 2) {
      // const sortedCriterias = criterias?.sort((a, b) => b?.weight - a?.weight);
      const sortedCriterias = [...criterias]?.sort((a, b) => {
        const aHasOrder = typeof a.order === "number";
        const bHasOrder = typeof b.order === "number";

        if (aHasOrder && bHasOrder) {
          // Both have order → sort ascending
          return a.order - b.order;
        } else if (!aHasOrder && !bHasOrder) {
          // Both missing order → sort by weight descending
          return b.weight - a.weight;
        } else if (aHasOrder) {
          // a has order but b doesn't → put a first
          return -1;
        } else {
          // b has order but a doesn't → put b first
          return 1;
        }
      });

      setCriterias(sortedCriterias);
    }
    setSteps(
      stepDescriptionsArr?.length - 1 === steps ? steps : steps + 1
    );
  };

  return (
    <div className={classes.bg}>
      {/* <div className={classes.mainContainer}> */}
      <Modal
        centered
        open={true}
        footer={null}
        bodyStyle={{ padding: "0" }}
        closable={null}
        width={1053}
      >
        <div className={classes.wrapper}>
          {isClosing && (
            <div className={classes.overlay_cancel_container}>
              <CautionIcon />
              <div>
                <p>
                  Closing this popup may result in the loss of unsaved data.
                </p>
                <p>Are you sure you want to proceed?"</p>
              </div>
              <div className={classes.btn_container}>
                <button
                  className={classes.close_button}
                  onClick={() => {
                    dispatch(ClosePopup(userDecisions?.id));
                  }}
                >
                  Close
                </button>
                <button
                  className={classes.cancel_button}
                  onClick={() => setIsClosing(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className={classes.header}>
            <span>Manage Criteria</span>
            <button
              type="button"
              onClick={() => setIsClosing(true)}
              className={classes.close}
              title="Close"
            >
              <CloseIcon2 />
            </button>
          </div>
          <div className={classes.separator_container}>
            <div className={classes.bg_color}></div>
            <div className={classes.separator}></div>
          </div>

          <div className={classes.details_container}>
            <h6>
              Step {steps + 1}: {handleStepCount(steps)}
            </h6>
            <span>
              Set up your decision by selecting criteria that fulfills your optimal decision.{" "}
            </span>
            {steps === 0 && (
              <div
                onClick={() => setIsGenerateCriteriaModal(true)}
                className={classes.generateWithAI}
              >
                Generate with AI
              </div>
            )}
          </div>

          <div className={classes.body}>
            <div className={classes.tableWrapper}>
              <Row className={cc(classes.table, classes.head)}>
                <Col span={10}>
                  <div className={classes.column}>
                    <div className={cc(classes.title, classes.first)}>
                      <span>Criteria</span>

                      {steps === 0 && (
                        <button
                          type="button"
                          className={classes.buttonAdd}
                          onClick={onAddCriteriaHandle}
                          disabled={loading || updateLoading}
                        >
                          <AddIcon2 />
                          <span>Add Criteria</span>
                        </button>
                      )}
                    </div>
                  </div>
                </Col>
                <Col span={14}>
                  <div className={classes.column}>
                    <div className={classes.title}>
                      <span>{handleCriteriaTitle()}</span>
                      {steps === 2 && (
                        <div
                          className={cc(classes.warning, {
                            [classes.equall]: totalWeight === 100,
                          })}
                        >
                          Sum of all weights: {Math.round(totalWeight)}%
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="characters" direction="vertical">
                  {(provided, snapshot) => (
                    <div
                      className="characters"
                      {...provided.droppableProps}
                      ref={provided?.innerRef}
                    >
                      {sortedCriteria &&
                        sortedCriteria
                          ?.filter((item) => !item?.is_deleted && item?.active)
                          ?.map((criteria, i) => (
                            <Draggable
                              key={i}
                              draggableId={`${i}`}
                              index={i}
                              isDragDisabled={steps !== 1}
                            >
                              {(swap, snapshot) => (
                                <div
                                  ref={swap.innerRef}
                                  {...swap.draggableProps}
                                  {...swap.dragHandleProps}
                                >
                                  <Row
                                    className={
                                      steps !== 0
                                        ? cc(
                                          classes.colored_table,
                                          classes.table
                                        )
                                        : classes.table
                                    }
                                    key={criteria?.id}
                                  >
                                    <Col span={10}>
                                      <div
                                        className={cc(
                                          classes.box,
                                          classes.first
                                        )}
                                      >
                                        <div
                                          className={classes.criteria}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          {steps === 1 && <MoveIcon />}
                                          <span
                                            className={criteriaNumStyleHandler()}
                                            style={{width:"28px"}}
                                          >
                                            {i + 1}
                                          </span>
                                          <div style={{ display: "flex", flexDirection: "column", width: "100%", marginTop: ids.includes(criteria?.id) ? "10px" : "null" }}>
                                            <textarea
                                              className={cc(
                                                classes.textarea,
                                                "criteria-name"
                                              )}
                                              placeholder={"Name..."}
                                              value={criteria?.title}
                                              onChange={(e) =>
                                                setField(
                                                  {
                                                    title: e?.target?.value,
                                                    id: criteria?.id,
                                                  },
                                                  "title"
                                                )
                                              }
                                              style={{ borderColor: ids.includes(criteria?.id) ? '#f51e14' : null }}
                                            >
                                            </textarea>
                                            {!criteria?.title && steps !== 0 && (
                                              <div
                                                style={{
                                                  color: "#f51e14",
                                                  fontSize: 12,
                                                  marginLeft: "5px",
                                                }}
                                              >
                                                This field cannot be empty.
                                              </div>
                                            )}
                                            {ids.includes(criteria?.id) && (
                                              <div
                                                style={{
                                                  color: "#f51e14",
                                                  fontSize: 12,
                                                  marginLeft: "5px",
                                                }}
                                              >
                                                Only 255 characters are allowed.
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                    <Col span={14}>
                                      {criteriaHandler(criteria, steps)}
                                    </Col>
                                  </Row>
                                </div>
                              )}
                            </Draggable>
                          ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            {inactiveCriterias.length && steps === 0 ? (
              <div className={classes.checkbox}>
                <Checkbox
                  onChange={onShowInactiveToggleHandle}
                  checked={isShowInactive}
                >
                  Show inactive criteria
                </Checkbox>
              </div>
            ) : null}

            <div className={`${classes.tableWrapper} mt-0`}>
              {isShowInactive && steps === 0 && (
                <>
                  {inactiveCriterias &&
                    inactiveCriterias?.map((criteria, i) => (
                      <Row
                        className={cc(classes.disabled_table, classes.table)}
                        key={criteria?.id}
                      >
                        <Col span={10}>
                          <div className={classes.box}>
                            <div
                              className={classes.criteria}
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {steps === 1 && <MoveIcon />}
                              <span className={criteriaNumStyleHandler()}>
                                {i + 1}
                              </span>
                              <input
                                className={classes.input}
                                placeholder={"Name..."}
                                value={criteria.title}
                                onChange={(e) =>
                                  setField(
                                    {
                                      title: e?.target?.value,
                                      id: criteria?.id,
                                    },
                                    "title"
                                  )
                                }
                              />
                            </div>
                          </div>
                        </Col>
                        <Col span={14}>
                          {criteriaHandler(criteria, steps, true)}
                        </Col>
                      </Row>
                    ))}
                </>
              )}
            </div>
          </div>

          <div className={classes.footer}>
            <div>
              {steps > 0 && <NavButton
                text={"Back"}
                icon={<BackIcon />}
                onClick={() => setSteps(steps === 0 ? steps : steps - 1)}
              />}
            </div>
            {steps === stepDescriptionsArr?.length - 1 ? (
              <SaveButton
                disabled={
                  totalWeight !== 100 || 
                  loading ||
                  updateLoading ||
                  rubricIds?.length > 0 ||
                  criterias?.some((item) => (!item.is_deleted && !item.title?.trim())) ||
                  ids?.length > 0
                }
                text={"Save"}
                onClick={saveCriterias}
                loading={loading || updateLoading}
                savingText={"Saving"}
              />
            ) : (
              <NavButton
                text={"Next"}
                icon={<NextIcon />}
                reverse={true}
                disabled={isNextButtonDisabled()}
                onClick={handleNextClick}
              />
            )}
          </div>
        </div>
      </Modal>
      {/* </div> */}

      {isGenerateCriteriaModal && (
        <AIGenerateCriteriaModal
          criteria={activeCriterias}
          decisionID={decisionId}
          decision={userDecisions?.problem}
          handleApply={onAddAICriteriaHandle}
          onClose={() => setIsGenerateCriteriaModal(false)}
        />
      )}
      {isRubricModal && (
        <AIScoringRubicModal
          decisionID={decisionId}
          criteria={activeCriterias}
          handleApply={onRubricApplyModal}
          onClose={() => setIsRubricModal(false)}
          selectedCriteria={selectedCriteria}
          onSelectedCriteriaChange={(name, value) => {
            let res = criterias?.find((item) => item?.id == value);
            setSelectedCriteria(res);
          }}
        // onSelectedCriteriaChange={(name, value) => setSelectedCriteria(value)}
        />
      )}
      {isDefinitionModal && (
        <AIDefinitionModal
          decisionID={decisionId}
          criteria={activeCriterias}
          handleApply={onDefinitionApplyModal}
          onClose={() => setIsDefinitionModal(false)}
          selectedCriteria={selectedCriteria}
          // onSelectedCriteriaChange={(name, value) => setSelectedCriteria(value)}
          onSelectedCriteriaChange={(name, value) => {
            let res = criterias?.find((item) => item?.id == value);
            setSelectedCriteria(res);
          }}
        />
      )}
      {!!isViewRubric && (
        <ScoringRubricModal
          criteria={activeCriterias}
          onSave={onRubricSaveHandle}
          onClose={() => setIsViewRubric(null)}
          optionCriteriaId={selectedCriteria?.id}
        />
      )}
    </div>
  );
};

export { ManageCriteria };
